import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Slug } from '../../_kernel/Slug'
import { PromotionTag } from './PromotionTag'
import { ListOfMarket } from '../../shared/domain/ListOfMarket'
import { ListOfLanguage } from '../../shared/domain/ListOfLanguage'
import { Market } from '../../shared/domain/Market'
import { Language } from '../../shared/domain/Language'
import type { GETCurrentPromoMarketingResponse } from '../infrastructure/HTTPMarketingRepository/schemas/promotions/active'

export const PromotionValidation = z.object({
  id: z.instanceof(Slug, { message: '[Promotion} id value is required' }),
  tag: z.instanceof(PromotionTag, { message: '[Promotion] tag is required' }),
  market: z.instanceof(Market, { message: '[Promotion] market is required' }),
  language: z.instanceof(Language, { message: '[Promotion] language is required' }),
  markets: z.instanceof(ListOfMarket).optional(),
  languages: z.instanceof(ListOfLanguage).optional(),
})

export class Promotion extends Model {
  static create({
    id,
    tag,
    market,
    language,
    markets,
    languages,
  }: z.infer<typeof PromotionValidation>) {
    PromotionValidation.parse({
      id,
      tag,
      market,
      language,
      markets,
      languages,
    })
    return new Promotion(id, tag, market, language, markets, languages)
  }

  static empty() {
    return new Promotion(Slug.empty(), PromotionTag.empty(), Market.empty(), Language.empty())
  }

  static fromJSONOrEmpty({ json }: { json?: ReturnType<Promotion['toJSON']> | null }) {
    if (!json) return Promotion.empty()

    return Promotion.create({
      id: Slug.create({ value: json.id }),
      tag: PromotionTag.fromJSON({ json: json.tag }),
      market: Market.fromJSON(json.market),
      language: Language.fromJSON(json.language),
      ...(json.markets && { markets: ListOfMarket.fromJSON({ json: json.markets }) }),
      ...(json.languages && { languages: ListOfLanguage.fromJSON({ json: json.languages }) }),
    })
  }

  static fromAPI({
    json,
    market,
    language,
  }: {
    json: NonNullable<GETCurrentPromoMarketingResponse>[number]
    market: Market
    language: Language
  }) {
    if (!json) return Promotion.empty()

    return Promotion.create({
      id: Slug.create({ value: json.slug }),
      tag: PromotionTag.fromJSON({ json: json.global.tags }),
      market,
      language,
    })
  }

  constructor(
    private readonly _id: Slug,
    private readonly _tag: PromotionTag,
    private readonly _market: Market,
    private readonly _language: Language,
    private readonly _markets?: ListOfMarket,
    private readonly _languages?: ListOfLanguage
  ) {
    super()
  }

  get id() {
    return this._id.value
  }

  get tag() {
    return this._tag
  }

  get markets() {
    return this._markets
  }

  get languages() {
    return this._languages
  }

  isEmpty() {
    return this._id.isEmpty()
  }

  toJSON() {
    return {
      id: this._id.value,
      tag: this._tag.toJSON(),
      market: this._market.toJSON(),
      language: this._language.toJSON(),
      ...(this._markets && { markets: this._markets.toJSON() }),
      ...(this._languages && { languages: this._languages.toJSON() }),
    }
  }
}
